import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateApartmentMetadata } from '@/api/generated/sdk.gen';
import type { UpdateApartmentMetadataRequest } from '@/api/generated/types.gen';
import type { Apartment } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// rawBody is a thin cast helper for endpoints where the generated TS body
// type doesn't allow explicit nulls but the wire contract does. The backend
// PATCH/{id} accepts explicit null to clear a column; @hey-api/openapi-ts
// drops the null arm of nullable optionals. Centralised here so future
// "send null via the typed client" callsites can find the convention.
// Exported: ApartmentContent's guest-facing-text save (description_long /
// house_rules / amenities) uses the SAME PATCH endpoint and needs the same
// explicit-null convention to clear a field it owns.
export function rawBody<T extends UpdateApartmentMetadataRequest>(b: Record<string, unknown>): T {
  return b as unknown as T;
}

export function OverviewSection({ apt, canWrite }: { apt: Apartment; canWrite: boolean }) {
  const [editing, setEditing] = useState(false);
  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Overview</h2>
        {!editing && canWrite && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {editing ? (
        <OverviewEditForm apt={apt} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} />
      ) : (
        <OverviewRead apt={apt} />
      )}
    </section>
  );
}

function OverviewRead({ apt }: { apt: Apartment }) {
  return (
    <>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
        <Field label="Guests included in the price" value={String(apt.occupancy_default)} />
        <Field label="Max adults" value={String(apt.occupancy_max)} />
        <Field label="Bedrooms" value={apt.bedrooms != null ? String(apt.bedrooms) : '—'} />
        <Field label="Bathrooms" value={apt.bathrooms != null ? String(apt.bathrooms) : '—'} />
        <Field label="Floor" value={apt.floor != null ? String(apt.floor) : '—'} />
        <Field label="Area" value={apt.area_sqm != null ? `${apt.area_sqm} m²` : '—'} />
        <Field label="Check-in from" value={apt.check_in_from ?? '—'} />
        <Field label="Check-out until" value={apt.check_out_until ?? '—'} />
        <Field label="Children allowed" value={apt.children_allowed ? 'Yes' : 'No'} />
        <Field label="Infants allowed" value={apt.infants_allowed ? 'Yes' : 'No'} />
        <Field label="Max infants" value={String(apt.max_infants)} />
        <Field
          label="Setup"
          value={
            apt.setup_complete ? (
              <span className="text-xs text-muted">Complete</span>
            ) : (
              <Badge
                variant="neutral"
                size="sm"
                title="Provisioning in progress; it finishes automatically."
              >
                Setting up
              </Badge>
            )
          }
        />
      </dl>

      <div className="mt-5 text-xs text-muted">
        Created {new Date(apt.created_at).toLocaleString()}
      </div>
    </>
  );
}

// Form state mirrors the editable PATCH columns. All-strings so empty input
// can map to "clear" (null). Numbers are parsed on submit.
interface FormState {
  nickname: string;
  bedrooms: string;
  bathrooms: string;
  area_sqm: string;
  floor: string;
  check_in_from: string;
  check_out_until: string;
  // House rules. children_allowed / infants_allowed are plain booleans (no
  // empty/clear state to represent as a string). max_infants is a NOT NULL
  // column with no clear semantics — see the comment on the PATCH body below.
  children_allowed: boolean;
  infants_allowed: boolean;
  max_infants: string;
  // occupancy_default / occupancy_max: NOT NULL columns, minimum 1, like
  // max_infants they have no "clear" state — but unlike max_infants (min 0,
  // coerced to 0 when emptied) 0 is never a valid occupancy, so an emptied
  // field is OMITTED from the PATCH body instead (server treats absent as
  // "leave unchanged"). See the body-building comment below.
  occupancy_default: string;
  occupancy_max: string;
}

function initialForm(apt: Apartment): FormState {
  return {
    nickname: apt.nickname ?? '',
    bedrooms: apt.bedrooms != null ? String(apt.bedrooms) : '',
    bathrooms: apt.bathrooms != null ? String(apt.bathrooms) : '',
    area_sqm: apt.area_sqm != null ? String(apt.area_sqm) : '',
    floor: apt.floor != null ? String(apt.floor) : '',
    check_in_from: apt.check_in_from ?? '',
    check_out_until: apt.check_out_until ?? '',
    children_allowed: apt.children_allowed,
    infants_allowed: apt.infants_allowed,
    max_infants: String(apt.max_infants),
    occupancy_default: String(apt.occupancy_default),
    occupancy_max: String(apt.occupancy_max),
  };
}

// strOrNull: empty string → null (clear column); otherwise the trimmed value.
// Exported: ApartmentContent's guest-facing-text save reuses this for
// description_long / house_rules against the same PATCH endpoint.
export function strOrNull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

// intOrNull parses an int string; "" → null. Returns undefined on a bad
// non-empty value so the caller can flag a validation error.
function intOrNull(v: string): number | null | undefined {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : undefined;
}

function floatOrNull(v: string): number | null | undefined {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
}

function OverviewEditForm({
  apt,
  onCancel,
  onSaved,
}: {
  apt: Apartment;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(() => initialForm(apt));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      // Build the patch body. We send every editable column on every save so
      // the server clears anything the operator emptied. Numeric/HH:MM
      // validation happens client-side here; backend re-checks HH:MM via
      // ErrInvalidTimeOfDay and returns 400.
      const bedrooms = intOrNull(form.bedrooms);
      const bathrooms = intOrNull(form.bathrooms);
      const floor = intOrNull(form.floor);
      const area = floatOrNull(form.area_sqm);
      if (bedrooms === undefined) throw new Error('Bedrooms must be a whole number.');
      if (bathrooms === undefined) throw new Error('Bathrooms must be a whole number.');
      if (floor === undefined) throw new Error('Floor must be a whole number.');
      if (area === undefined) throw new Error('Area must be a number.');

      // max_infants is a NOT NULL column (comment on UpdateApartmentMetadataRequest:
      // "optional but not nullable — there is no way to clear them, only to set
      // a new value"). An emptied input can't map to null like the other numeric
      // fields do; coerce it to 0 instead of sending a value the backend rejects.
      const maxInfantsRaw = intOrNull(form.max_infants);
      if (maxInfantsRaw === undefined) throw new Error('Max infants must be a whole number.');
      const maxInfants = maxInfantsRaw ?? 0;

      // occupancy_default / occupancy_max: also NOT NULL, minimum 1. Unlike
      // max_infants there is no sane coercion for an emptied field — 0 is
      // never a valid occupancy and the server would reject it with a
      // message the operator did nothing to earn. So an emptied field is
      // OMITTED from the body entirely (the backend's per-field Set flag
      // treats absent as "leave unchanged"), not sent as null or 0. Base-
      // above-max / base-below-1 validation happens server-side; we surface
      // its 400 detail via errorMessage rather than re-implementing it here.
      const occupancyDefaultRaw = intOrNull(form.occupancy_default);
      const occupancyMaxRaw = intOrNull(form.occupancy_max);
      if (occupancyDefaultRaw === undefined) {
        throw new Error('Guests included in the price must be a whole number.');
      }
      if (occupancyMaxRaw === undefined) throw new Error('Max adults must be a whole number.');

      // @hey-api/openapi-ts collapses `nullable: true` to just `field?: T`
      // (drops the `| null` arm). Backend accepts and acts on JSON null
      // (Set=true, Null=true → clear column). Cast through the rawBody
      // helper so the wire payload preserves explicit nulls.
      //
      // description_long / house_rules / amenities are NOT sent here — they
      // moved to the Content tab (ApartmentContent's guest-text save). The
      // backend PATCH is a true partial patch (per-field `Set` flag), so
      // omitting these keys entirely leaves them untouched server-side;
      // only the fields Overview still owns are sent.
      const body = rawBody({
        nickname: strOrNull(form.nickname),
        bedrooms,
        bathrooms,
        area_sqm: area,
        floor,
        check_in_from: strOrNull(form.check_in_from),
        check_out_until: strOrNull(form.check_out_until),
        children_allowed: form.children_allowed,
        infants_allowed: form.infants_allowed,
        max_infants: maxInfants,
        ...(occupancyDefaultRaw !== null ? { occupancy_default: occupancyDefaultRaw } : {}),
        ...(occupancyMaxRaw !== null ? { occupancy_max: occupancyMaxRaw } : {}),
      });
      return updateApartmentMetadata({
        throwOnError: true,
        path: { id: apt.id },
        body,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartments', apt.id] });
      qc.invalidateQueries({ queryKey: ['apartments'] });
      toast({ kind: 'success', title: 'Apartment updated', message: apt.title });
      onSaved();
    },
    onError: (err) => {
      setError(errorMessage(err));
    },
  });

  const set = <K extends keyof FormState>(k: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
  };

  const setChecked = (k: 'children_allowed' | 'infants_allowed') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [k]: e.target.checked }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mut.mutate();
      }}
    >
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="Guests included in the price" id="apt-occupancy-default">
            <input
              id="apt-occupancy-default"
              type="number"
              min={1}
              value={form.occupancy_default}
              onChange={set('occupancy_default')}
              className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
            />
          </FormField>
          <FormField label="Max adults" id="apt-occupancy-max">
            <input
              id="apt-occupancy-max"
              type="number"
              min={1}
              value={form.occupancy_max}
              onChange={set('occupancy_max')}
              className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
            />
          </FormField>
        </div>
        <p className="mt-2 text-xs text-muted">
          Guests above the included count are charged the extra-guest fee, if the pricing strategy has one.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
        <FormField label="Nickname" id="apt-nickname">
          <input
            id="apt-nickname"
            type="text"
            value={form.nickname}
            onChange={set('nickname')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Bedrooms" id="apt-bedrooms">
          <input
            id="apt-bedrooms"
            type="number"
            min={0}
            value={form.bedrooms}
            onChange={set('bedrooms')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Bathrooms" id="apt-bathrooms">
          <input
            id="apt-bathrooms"
            type="number"
            min={0}
            value={form.bathrooms}
            onChange={set('bathrooms')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Floor" id="apt-floor">
          <input
            id="apt-floor"
            type="number"
            value={form.floor}
            onChange={set('floor')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Area (m²)" id="apt-area">
          <input
            id="apt-area"
            type="number"
            step="0.01"
            min={0}
            value={form.area_sqm}
            onChange={set('area_sqm')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Check-in from (HH:MM)" id="apt-checkin">
          <input
            id="apt-checkin"
            type="time"
            value={form.check_in_from}
            onChange={set('check_in_from')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Check-out until (HH:MM)" id="apt-checkout">
          <input
            id="apt-checkout"
            type="time"
            value={form.check_out_until}
            onChange={set('check_out_until')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Max infants" id="apt-max-infants">
          <input
            id="apt-max-infants"
            type="number"
            min={0}
            value={form.max_infants}
            onChange={set('max_infants')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="apt-children-allowed" className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
          <input
            id="apt-children-allowed"
            type="checkbox"
            checked={form.children_allowed}
            onChange={setChecked('children_allowed')}
            className="rounded border-[var(--border-hairline)]"
          />
          Children allowed
        </label>
        <label htmlFor="apt-infants-allowed" className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
          <input
            id="apt-infants-allowed"
            type="checkbox"
            checked={form.infants_allowed}
            onChange={setChecked('infants_allowed')}
            className="rounded border-[var(--border-hairline)]"
          />
          Infants allowed
        </label>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
          Save
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function FormField({
  label,
  id,
  className = '',
  children,
}: {
  label: string;
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

export function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
